import React, { useState, useEffect, useCallback } from 'react';
import { AuthProvider } from './contexts/AuthContext';
import { CanvasProvider } from './contexts/CanvasContext';
import { useAuth } from './hooks/useAuth';
import { usePresence } from './hooks/usePresence';
import Login from './components/Auth/Login';
import Signup from './components/Auth/Signup';
import Canvas from './components/Canvas/Canvas';
import CanvasControls from './components/Canvas/CanvasControls';
import Navbar from './components/Layout/Navbar';
import ErrorBoundary from './components/Common/ErrorBoundary';
import ErrorToastContainer from './components/Common/ErrorToast';
import ConnectionStatus from './components/Common/ConnectionStatus';
import AIPromptPanel from './components/AI/AIPromptPanel';

// Loading component
function LoadingSpinner() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          CollabCanvas
        </h2>
        <p className="text-gray-600">
          Loading your workspace...
        </p>
      </div>
    </div>
  );
}

// Canvas Workspace Component (handles presence)
function CanvasWorkspace() {
  // Initialize presence tracking for collaborative features - CALL ONCE AT TOP LEVEL
  const onlineUsers = usePresence();
  const [showShortcuts, setShowShortcuts] = useState(false);
  
  console.log('👥 Canvas workspace loaded with', onlineUsers.length, 'users online');
  
  // Stable callbacks wrapped in useCallback to prevent Navbar re-renders
  const handleShowShortcuts = useCallback(() => {
    setShowShortcuts(true);
  }, []);
  
  const handleCloseShortcuts = useCallback(() => {
    setShowShortcuts(false);
  }, []);
  
  // Listen for keyboard shortcut to show shortcuts
  useEffect(() => {
    window.addEventListener('showShortcuts', handleShowShortcuts);
    return () => window.removeEventListener('showShortcuts', handleShowShortcuts);
  }, [handleShowShortcuts]);
  
  return (
    <ErrorBoundary name="CanvasWorkspace">
      <CanvasProvider>
        <div className="h-screen flex flex-col">
          {/* Navbar at top - PASS onlineUsers as prop */}
          <Navbar onShowShortcuts={handleShowShortcuts} onlineUsers={onlineUsers} />
          
          {/* Canvas area filling rest of viewport */}
          <div className="flex-1 relative">
            <Canvas 
              showShortcuts={showShortcuts}
              onCloseShortcuts={handleCloseShortcuts}
            />
            
            {/* Canvas controls overlaying canvas */}
            <CanvasControls />
            
            {/* AI Prompt Panel - floating at bottom center */}
            <AIPromptPanel />
          </div>
          
          {/* Connection status indicator */}
          <ConnectionStatus />
        </div>
      </CanvasProvider>
    </ErrorBoundary>
  );
}

// Main App Content (inside AuthProvider)
function AppContent() {
  const { currentUser, loading } = useAuth();
  const [showSignup, setShowSignup] = useState(false);

  // Show loading spinner while checking auth state
  if (loading) {
    return <LoadingSpinner />;
  }

  // Show Canvas workspace if user is authenticated
  if (currentUser) {
    return <CanvasWorkspace />;
  }

  // Show Login or Signup based on state
  if (showSignup) {
    return (
      <Signup 
        onToggleToLogin={() => setShowSignup(false)} 
      />
    );
  }

  return (
    <Login 
      onToggleToSignup={() => setShowSignup(true)} 
    />
  );
}

// Main App Component
function App() {
  return (
    <ErrorBoundary name="App">
      <AuthProvider>
        <AppContent />
        
        {/* Global error toast container */}
        <ErrorToastContainer />
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;